/**
 * purchase-options.js
 *
 * Native, Shopify-driven OTP / Subscribe widget. Replaces the legacy
 * Recharge JS widget on the PDP. Reads variant data from the sibling
 * <variant-radios> / <variant-selects> JSON blob, updates row prices,
 * the BUY NOW button price, the savings badge, and the optional
 * additional_product add-on. Submits `selling_plan` through the existing
 * Shopify product form.
 */
(function () {
  if (window.__PurchaseOptionsInitialized) return;
  window.__PurchaseOptionsInitialized = true;

  const REGISTRY = new Map();
  window.PurchaseOptionsRegistry = REGISTRY;

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const dollars = (cents / 100).toFixed(2);
    const [whole, decimal] = dollars.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `$${withCommas}.${decimal}`;
  }

  class PurchaseOptions {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productHandle = root.dataset.productHandle || '';
      this.defaultOption = root.dataset.default || 'subscribe_and_save';
      this.requiresSellingPlan = root.dataset.requiresSellingPlan === 'true';

      this.productScope = document.getElementById(`ProductInfo-${this.sectionId}`) || document;
      this.priceWrapper = document.getElementById(`price-${this.sectionId}`);
      this.frequencySelect = root.querySelector('[data-purchase-option-frequency]');
      this.discountEl = root.querySelector('[data-purchase-option-discount]');
      this.otpPriceEl = root.querySelector('[data-purchase-option-price="one_time_purchase"]');
      this.subPriceEl = root.querySelector('[data-purchase-option-price="subscribe_and_save"]');

      // Single-variant PDPs render no variant picker, so fall back to the
      // blob the purchase-options snippet emits for exactly that case.
      this.variants =
        this.readJSON(this.findVariantSelectsScript()) ||
        this.readJSON(root.querySelector('[data-purchase-option-variants-json]'));
      this.planGroups = this.readJSON(root.querySelector('[data-purchase-option-plans-json]'));

      this.currentVariant = this.getInitialVariant();

      this.bindEvents();
      this.syncInitialState();

      REGISTRY.set(String(this.sectionId), this);
    }

    findVariantSelectsScript() {
      const host =
        document.querySelector(`variant-radios[data-section="${this.sectionId}"]`) ||
        document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!host) return null;
      return host.querySelector('script[type="application/json"]');
    }

    readJSON(node) {
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (_e) {
        return null;
      }
    }

    getInitialVariant() {
      if (!Array.isArray(this.variants)) return null;
      const idInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      const fromForm = idInput ? Number(idInput.value) : null;
      if (fromForm) {
        const match = this.variants.find((v) => Number(v.id) === fromForm);
        if (match) return match;
      }
      return this.variants.find((v) => v.available) || this.variants[0] || null;
    }

    bindEvents() {
      this.root.addEventListener('change', (e) => {
        if (e.target.matches('[data-purchase-option-input]')) {
          this.onPurchaseOptionChange(e.target.value);
        } else if (e.target.matches('[data-purchase-option-frequency]')) {
          this.onFrequencyChange(e);
        }
      });

      this.root.addEventListener('click', (e) => {
        if (e.target.matches('[data-purchase-option-frequency]')) {
          e.stopPropagation();
          return;
        }
        const row = e.target.closest('[data-purchase-option-row]');
        if (!row) return;
        const input = row.querySelector('[data-purchase-option-input]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    syncInitialState() {
      if (!this.currentVariant) return;
      this.updateRows();
      this.syncSellingPlanInput(this.getSelected());
      this.applySelectionSideEffects(this.getSelected());
    }

    getSelected() {
      const checked = this.root.querySelector('[data-purchase-option-input]:checked');
      if (this.requiresSellingPlan) return 'subscribe_and_save';
      return checked ? checked.value : this.defaultOption;
    }

    getSelectedSellingPlanId() {
      if (!this.frequencySelect) return '';
      return this.frequencySelect.value || '';
    }

    getAllocation(variant, sellingPlanId) {
      if (!variant || !sellingPlanId) return null;
      const list = variant.selling_plan_allocations || [];
      return list.find((a) => String(a.selling_plan_id) === String(sellingPlanId)) || null;
    }

    getFirstAllocation(variant) {
      const list = (variant && variant.selling_plan_allocations) || [];
      return list[0] || null;
    }

    getDisplayPrice(variant, sellingPlanId) {
      if (!variant) return 0;
      if (!sellingPlanId) return variant.price;
      const alloc = this.getAllocation(variant, sellingPlanId);
      return alloc ? alloc.price : variant.price;
    }

    getDiscountPct(variant, sellingPlanId) {
      if (!variant || !variant.price) return 0;
      const sub = this.getDisplayPrice(variant, sellingPlanId);
      if (sub >= variant.price) return 0;
      return Math.round(((variant.price - sub) / variant.price) * 100);
    }

    onPurchaseOptionChange(value) {
      if (this.requiresSellingPlan) value = 'subscribe_and_save';

      this.root
        .querySelectorAll('[data-purchase-option-row]')
        .forEach((row) =>
          row.classList.toggle('purchase-option--active', row.dataset.purchaseOptionRow === value)
        );

      this.syncSellingPlanInput(value);
      this.updateButtonPrice();
      this.applySelectionSideEffects(value);
    }

    onFrequencyChange() {
      if (this.getSelected() === 'one_time_purchase') {
        const subInput = this.root.querySelector(
          '[data-purchase-option-input][value="subscribe_and_save"]'
        );
        if (subInput && !subInput.checked) {
          subInput.checked = true;
          this.root
            .querySelectorAll('[data-purchase-option-row]')
            .forEach((row) =>
              row.classList.toggle(
                'purchase-option--active',
                row.dataset.purchaseOptionRow === 'subscribe_and_save'
              )
            );
          this.syncSellingPlanInput('subscribe_and_save');
          this.applySelectionSideEffects('subscribe_and_save');
        }
      }
      this.updateRows();
      this.updateButtonPrice();
    }

    onVariantChange(variant) {
      if (!variant) return;
      this.currentVariant = variant;

      const hasPlans =
        Array.isArray(variant.selling_plan_allocations) &&
        variant.selling_plan_allocations.length > 0;

      if (!hasPlans) {
        this.root.hidden = true;
        this.clearSellingPlanField();
        this.updateButtonPrice();
        this.applySelectionSideEffects('one_time_purchase');
        return;
      }
      this.root.hidden = false;

      this.refreshFrequencyOptions(variant);
      this.updateRows();
      this.syncSellingPlanInput(this.getSelected());
      this.updateButtonPrice();
      this.applySelectionSideEffects(this.getSelected());
    }

    refreshFrequencyOptions(variant) {
      if (!this.frequencySelect) return;
      const previousId = this.frequencySelect.value;
      const allocations = variant.selling_plan_allocations || [];
      if (!allocations.length) return;

      const nameLookup = this.buildPlanNameLookup();
      const frag = document.createDocumentFragment();
      allocations.forEach((alloc) => {
        const opt = document.createElement('option');
        opt.value = String(alloc.selling_plan_id);
        const planName =
          (alloc.selling_plan && alloc.selling_plan.name) ||
          nameLookup[String(alloc.selling_plan_id)] ||
          '';
        opt.textContent = planName;
        opt.dataset.planId = String(alloc.selling_plan_id);
        frag.appendChild(opt);
      });
      this.frequencySelect.innerHTML = '';
      this.frequencySelect.appendChild(frag);

      const hasPrevious = allocations.some(
        (a) => String(a.selling_plan_id) === String(previousId)
      );
      this.frequencySelect.value = hasPrevious ? previousId : String(allocations[0].selling_plan_id);
    }

    buildPlanNameLookup() {
      const lookup = {};
      if (!Array.isArray(this.planGroups)) return lookup;
      this.planGroups.forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          lookup[String(plan.id)] = plan.name;
        });
      });
      return lookup;
    }

    updateRows() {
      if (!this.currentVariant) return;
      const v = this.currentVariant;
      const planId = this.getSelectedSellingPlanId();

      if (this.otpPriceEl) this.otpPriceEl.textContent = formatMoney(v.price);
      if (this.subPriceEl)
        this.subPriceEl.textContent = formatMoney(this.getDisplayPrice(v, planId));

      if (this.discountEl) {
        const pct = this.getDiscountPct(v, planId);
        this.discountEl.textContent = pct > 0 ? `(${pct}%)` : '';
      }
    }

    syncSellingPlanInput(value) {
      if (!this.frequencySelect) return;
      if (value === 'subscribe_and_save') {
        this.frequencySelect.setAttribute('name', 'selling_plan');
        if (!this.frequencySelect.value) {
          const first = this.getFirstAllocation(this.currentVariant);
          if (first) this.frequencySelect.value = String(first.selling_plan_id);
        }
      } else {
        this.frequencySelect.removeAttribute('name');
      }
    }

    clearSellingPlanField() {
      if (this.frequencySelect) {
        this.frequencySelect.innerHTML = '';
        this.frequencySelect.removeAttribute('name');
      }
    }

    updateButtonPrice() {
      if (!this.priceWrapper || !this.currentVariant) return;
      const v = this.currentVariant;
      const selected = this.getSelected();
      const planId = selected === 'subscribe_and_save' ? this.getSelectedSellingPlanId() : '';
      const display = this.getDisplayPrice(v, planId);
      const text = formatMoney(display);

      const regulars = this.priceWrapper.querySelectorAll(
        '.price__regular .price-item--regular, .price__sale .price-item--last, .price__sale .price-item--sale'
      );
      regulars.forEach((node) => {
        node.textContent = text;
      });

      this.priceWrapper.style.opacity = '1';
      this.priceWrapper.classList.remove('visibility-hidden');
    }

    applySelectionSideEffects(value) {
      this.toggleSavingsBadge(value);
      this.toggleAddOn(value);
    }

    toggleSavingsBadge(value) {
      const wrapper = document.querySelector(`#ProductInfo-${this.sectionId} .selection-savings`);
      if (!wrapper) return;
      const def = wrapper.querySelectorAll('.def_var');
      const sns = wrapper.querySelectorAll('.sns_var');
      const showSub = value === 'subscribe_and_save';
      def.forEach((el) => (el.style.display = showSub ? 'none' : ''));
      sns.forEach((el) => (el.style.display = showSub ? 'inline-block' : 'none'));
    }

    toggleAddOn(value) {
      const wrapper = document.querySelector(`#ProductInfo-${this.sectionId} .add_pro_wrapper`);
      const checkbox = document.querySelector(`#ProductInfo-${this.sectionId} .checkbox_add`);
      if (!wrapper || !checkbox) return;

      const enable = value === 'one_time_purchase';
      wrapper.classList.add('box-transition');
      if (enable) {
        checkbox.checked = true;
        // Force layout so the CSS transition sees the new state.
        // eslint-disable-next-line no-unused-expressions
        wrapper.clientWidth;
        wrapper.classList.remove('box-hidden');
      } else {
        checkbox.checked = false;
        wrapper.classList.add('box-hidden');
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-purchase-options]').forEach((root) => {
      if (root.dataset.purchaseOptionsReady === 'true') return;
      root.dataset.purchaseOptionsReady = 'true';
      try {
        new PurchaseOptions(root);
      } catch (err) {
        console.error('[purchase-options] init failed', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
